/**
 * @fileoverview "Draft with AI" toolbar control for `NotesEditor`.
 *
 * The practitioner describes the note they want; Claude drafts it; the
 * draft is previewed in the popover and only written into the editor on an
 * explicit Insert. Existing note content is never touched until then: the
 * draft is inserted at the cursor (after any selection, never replacing
 * it) as one history step, so ⌘Z — or the toast's Undo — reverts it.
 */

import { useCallback, useRef, useState } from "react";
import { useAction } from "convex/react";
import type { Editor } from "@tiptap/react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  aiErrorMessage,
  DRAFT_PROMPT_MAX_CHARS,
  draftTextToHtml,
} from "./notes-editor.helpers";

interface NotesDraftPopoverProps {
  /** The notes editor the accepted draft is inserted into. */
  editor: Editor;
}

/**
 * Toolbar trigger plus popover for drafting a note with AI: a description
 * textarea, the drafted preview, and Cancel / Draft (Redraft) / Insert
 * controls. Closing the popover by any route other than Insert discards the
 * prompt and the draft.
 */
export function NotesDraftPopover({ editor }: NotesDraftPopoverProps) {
  const draftNote = useAction(api.contacts.notesAi.draftNote);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read by Radix's focus-outside handler, which can fire between our
  // state update and the re-render that would refresh a closure.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  // Identifies the popover session a request belongs to. Dismissing the
  // popover (or starting a new request) bumps it, so a response that lands
  // afterwards is dropped instead of populating a fresh session (F03).
  const requestIdRef = useRef(0);
  // Set when the popover closes because a draft was inserted, so focus
  // stays in the editor rather than returning to the trigger (F04).
  const insertedRef = useRef(false);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setPrompt("");
    setDraft(null);
    setError(null);
    setBusy(false);
  }, []);

  const onOpenChange = useCallback(
    (next: boolean) => {
      // Closing by any route (Cancel, Escape, click-away) discards the draft.
      if (!next) reset();
      setOpen(next);
    },
    [reset],
  );

  const promptTooLong = prompt.trim().length > DRAFT_PROMPT_MAX_CHARS;

  /**
   * Sends the description to `draftNote` and shows the result as the preview.
   * Each call takes a fresh id from `requestIdRef`; a response (or error)
   * whose id no longer matches — because the popover was dismissed or a
   * Redraft started meanwhile — is dropped rather than applied to the newer
   * session. No-op while a request is in flight or the prompt is invalid.
   */
  const generate = async () => {
    const trimmed = prompt.trim();
    if (trimmed.length === 0 || promptTooLong || busy) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    // Park focus on the read-only prompt before the clicked Draft button
    // becomes disabled: a focused control that gets disabled drops focus to
    // <body>, and inside a modal dialog Radix's focus trap then takes it,
    // which the popover would read as focus-outside and dismiss (F06).
    promptRef.current?.focus();
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await draftNote({ prompt: trimmed });
      if (requestIdRef.current !== requestId) return;
      setDraft(result.text.trim());
    } catch (err) {
      if (requestIdRef.current !== requestId) return;
      setError(aiErrorMessage(err, "AI draft failed. Try again."));
    } finally {
      if (requestIdRef.current === requestId) setBusy(false);
    }
  };

  /**
   * Writes the previewed draft into the editor at the end of the current
   * selection, so nothing the practitioner selected is replaced. The
   * insertion is one `insertContentAt` transaction, hence one history step:
   * ⌘Z, or the toast's Undo, removes the whole draft and nothing else. The
   * popover then closes with focus handed to the editor (see `insertedRef`).
   */
  const insert = () => {
    if (!draft) return;
    const html = draftTextToHtml(draft);
    if (html.length === 0) return;
    // After the selection, so nothing the practitioner selected is replaced.
    const at = editor.state.selection.to;
    editor.chain().focus().insertContentAt(at, html).run();
    insertedRef.current = true;
    setOpen(false);
    reset();
    const toastId = toast.success("Draft inserted", {
      action: {
        label: "Undo",
        onClick: () => editor.chain().focus().undo().run(),
      },
    });
    // Undo reverts the latest history step, so the offer is only honest
    // while the insertion is that step. The first edit afterwards (typing,
    // or ⌘Z itself) withdraws it.
    const dismissOnEdit = () => {
      toast.dismiss(toastId);
      editor.off("update", dismissOnEdit);
    };
    editor.on("update", dismissOnEdit);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Draft with AI"
          title="Describe the note you want and let AI draft it"
          className="h-7 gap-1 px-2 text-xs"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          Draft
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(24rem,calc(100vw-2rem))] p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // While a draft is in flight nothing in here may steal-close us:
        // inside a modal dialog, Radix's focus trap re-homes focus to the
        // dialog whenever focus is on <body>, which would read as
        // focus-outside and dismiss the popover mid-request (F06).
        onFocusOutside={(e) => {
          if (busyRef.current) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          // Radix returns focus to the trigger on close. After Insert the
          // practitioner expects to keep typing (or ⌘Z) in the editor.
          if (!insertedRef.current) return;
          insertedRef.current = false;
          e.preventDefault();
          editor.commands.focus();
        }}
      >
        <div className="flex flex-col gap-2">
          <label className="text-xs font-medium text-muted-foreground">
            Describe the note
            <Textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  void generate();
                }
              }}
              placeholder="e.g. Called the director about the outstanding BAS; he'll send the bank statements by Friday."
              // readOnly, not disabled: a disabled control drops focus to
              // <body>, which lets an enclosing dialog's focus trap take
              // over and dismiss the popover (F06).
              readOnly={busy}
              aria-busy={busy}
              maxLength={DRAFT_PROMPT_MAX_CHARS}
              className="mt-1 min-h-[72px] text-sm"
              autoFocus
            />
          </label>
          {(promptTooLong || prompt.length >= DRAFT_PROMPT_MAX_CHARS - 200) && (
            <p
              className={
                promptTooLong
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {promptTooLong
                ? `Description is too long (${prompt.trim().length.toLocaleString()} characters; limit ${DRAFT_PROMPT_MAX_CHARS.toLocaleString()}).`
                : `${prompt.length.toLocaleString()} / ${DRAFT_PROMPT_MAX_CHARS.toLocaleString()} characters`}
            </p>
          )}

          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}

          {draft !== null && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Draft preview
              </span>
              <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                {draft}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={draft === null ? "default" : "outline"}
                size="sm"
                onClick={() => void generate()}
                disabled={busy || promptTooLong || prompt.trim().length === 0}
                className="gap-1"
              >
                {busy ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {busy ? "Drafting…" : draft === null ? "Draft" : "Redraft"}
              </Button>
              {draft !== null && (
                <Button
                  type="button"
                  size="sm"
                  onClick={insert}
                  disabled={busy}
                >
                  Insert
                </Button>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
